import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.scss'
import Button from '../../components/Button'
import Table from '../../components/Table'
import { BiTrash } from 'react-icons/bi'
import { IoIosAdd } from 'react-icons/io'
import { AiOutlineEdit } from 'react-icons/ai'
import Confirmation from '../../components/Confirmation/Confirmation'
import Loader from '../../components/Loader'
import Empty from '../../components/Empty'
import { Select } from '../../components/ModalInputs'
import { getSchoolYears } from '../../actions/common'
import {
  deleteApplicantGroup,
  getApplicantAddList,
  getApplicantAddNameExams,
  getApplicantGroupList,
  getApplicantNameExams,
  getApplicantPhysicalTraining
} from '../../actions/applicantGroup'
import { getExamCityList } from '../../actions/exam_city'
import ApplicantGroupModal from '../ApplicantGroup/Modal'

const GroupsModal = ({ open, closeModal }) => {
  const dispatch = useDispatch()
  const { list, loading } = useSelector(state => state.applicant_group)
  const { schoolYears } = useSelector(state => state.common)

  const [params, setParams] = useState({ school_year: 0 })
  const [confirmation, setConfirmation] = useState(false)
  const [mode, setMode] = useState('general')
  const [selectedId, setSelectedId] = useState(null)
  const [editModalId, setEditModalId] = useState(null)

  useEffect(() => {
    if (!open) return
    dispatch(getExamCityList())
    dispatch(getApplicantNameExams())
    dispatch(getApplicantPhysicalTraining())
    dispatch(getApplicantAddNameExams())
    dispatch(getApplicantAddList())
    dispatch(getSchoolYears())
  }, [open])

  useEffect(() => {
    if (schoolYears.length && open) {
      const actualYear = schoolYears?.find(el => el.actual === true)?.xp_key
      if (actualYear) {
        setParams(prev => ({ ...prev, school_year: actualYear }))
      }
    }
  }, [schoolYears, open])

  useEffect(() => {
    if (params.school_year && open) {
      dispatch(getApplicantGroupList(params))
    }
  }, [params.school_year, open])

  const handleGetData = () => {
    if (params.school_year === 0) return
    dispatch(getApplicantGroupList(params))
  }

  const handleChange = (e) => {
    setParams(prev => ({ ...prev, [e.target.name]: Number(e.target.value) }))
  }

  const acceptConfirmation = () => {
    dispatch(deleteApplicantGroup(confirmation.id, params))
    setConfirmation(false)
  }

  const rejectConfirmation = () => {
    setConfirmation(false)
  }

  const [confirmClose, setConfirmClose] = useState(false)
  const handleClose = useCallback(() => setConfirmClose(true), [])
  const acceptCloseConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
  const rejectCloseConfirmation = useCallback(() => setConfirmClose(false), [])

  const openEditModal = (id) => () => {
    setEditModalId(id)
  }

  const closeEditModal = () => {
    setEditModalId(null)
    setMode('general')
    dispatch(getApplicantAddList())
  }

  const handleRowClick = (id) => () => {
    setSelectedId(id)
  }

  const selectedCount = selectedId ? 1 : 0

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        showCloseIcon={false}
        classNames={{ modal: styles.groupsModal }}
        closeOnEsc={true}
        closeOnOverlayClick={true}
        animationDuration={1}
      >
        <div className={styles.groupsModalHeader}>
          Группы претендентов
        </div>

        <div className={styles.groupsModalContent}>
          {confirmation && (
            <Confirmation
              top="30px"
              left="40px"
              title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
              acceptConfirmation={() => acceptConfirmation()}
              rejectConfirmation={rejectConfirmation}
            />
          )}

          <div className={styles.groupsModalFilters}>
            <Select
              label='Год поступления'
              name='school_year'
              value={params.school_year}
              onChange={handleChange}
            >
              {schoolYears?.map(el => (
                <option key={el.xp_key} value={el.xp_key}>{el.name}</option>
              ))}
            </Select>
            <Button onClick={handleGetData}>Обновить</Button>
          </div>

          {loading && <div className={styles.groupsModalLoader}><Loader /></div>}

          {!loading && !list?.length ? (
            <Empty />
          ) : (
            <Table className={styles.groupsModalTable}>
              <thead>
                <tr>
                  <th>Наименование</th>
                  <th>Год набора</th>
                  <th>Набор</th>
                  <th>Класс для поступления</th>
                  <th>Кол-во участников</th>
                  <th>Статус</th>
                  <th width='1.5rem'>Удалить</th>
                </tr>
              </thead>
              <tbody>
                {list?.map((item) => {
                  const { id, name, school_year, stream, class_year, applicant_count, closed } = item
                  const isSelected = selectedId === id
                  return (
                    <tr
                      key={id}
                      className={isSelected ? styles.selectedRow : ''}
                      onClick={handleRowClick(id)}
                      onDoubleClick={openEditModal(id)}
                    >
                      <td>{name}</td>
                      <td>{school_year}</td>
                      <td>{stream}</td>
                      <td>{class_year}</td>
                      <td>{applicant_count}</td>
                      <td>{closed}</td>
                      <td>
                        <i
                          className={styles.trash}
                          onClick={(e) => {
                            e.stopPropagation()
                            setConfirmation({ id, name })
                          }}
                        >
                          <BiTrash />
                        </i>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}

          <div className={styles.groupsModalStats}>
            <span>Всего строк: {list?.length || 0}</span>
            <span>Выделено строк: {selectedCount}</span>
          </div>
        </div>

        <div className={styles.groupsModalFooter}>
          <div className={styles.groupsModalFooterLeft}>
            <Button mode='bordered' onClick={openEditModal('add')}>
              <IoIosAdd size={20} />
              Добавить
            </Button>
            <Button mode='bordered' onClick={openEditModal(selectedId)} disabled={!selectedId}>
              <AiOutlineEdit size={18} />
              Изменить
            </Button>
            <Button
              mode='bordered'
              onClick={() => selectedId && setConfirmation({ id: selectedId, name: list?.find(i => i.id === selectedId)?.name })}
              disabled={!selectedId}
            >
              <BiTrash size={18} />
              Удалить
            </Button>
            <Button mode='bordered'>Извещения</Button>
            <Button mode='bordered'>Печать</Button>
            <Button mode='bordered'>Загрузить результаты</Button>
          </div>
          <Button mode='white' onClick={closeModal}>Закрыть</Button>
        </div>
        {confirmClose && (
          <Confirmation
            title="Закрыть без сохранения изменений?"
            acceptConfirmation={acceptCloseConfirmation}
            rejectConfirmation={rejectCloseConfirmation}
          />
        )}
      </Modal>

      <ApplicantGroupModal
        id={editModalId}
        params={params}
        closeModal={closeEditModal}
        mode={mode}
        setMode={setMode}
      />
    </>
  )
}

export default GroupsModal
